import {
  useCallback,
  useEffect,
  useEffectEvent,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import styles from "./TaskViewerSheet.module.css";
import {
  PatchScheduleTask,
  PatchTask,
} from "@coworker/apprestructured/src/task-management/types/Task";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { useUID } from "@coworker/app/src/core/auth/useLoggedInUser";
import CloseSheet, {
  CloseReasonOption,
} from "@coworker/apprestructured/src/task-management/components/CloseSheet/CloseSheet";
import { useTranslation } from "@coworker/locales";
import useSaveGuard from "@coworker/apprestructured/src/save-guard/useSaveGuard";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import { NotFound } from "../NotFound/NotFound";
import { useUpdateTask } from "@coworker/apprestructured/src/task-management/services/useUpdateTask";
import { useAllCloseReasons } from "@coworker/apprestructured/src/task-management/services/useAllCloseReasons";
import {
  ViewerFragmentCommonProps,
  ViewerFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/ViewerFragment";
import { LOADING } from "@coworker/apprestructured/src/task-management/loading";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { useTask } from "@coworker/apprestructured/src/task-management/services/useTask";
import { merge } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import TitledText from "@coworker/apprestructured/src/task-management/components/TitledText/TitledText";
import { Comment } from "../../types/Comment";
import { useCreateComment } from "../../services/useCreateComment";
import { useUser } from "@coworker/apprestructured/src/shared/hooks/users/useUser";
import { Images } from "@coworker/apprestructured/src/task-management/components/Images/Images";
import { useTaskImages } from "@coworker/apprestructured/src/task-management/services/useTaskImages";
import { useImageUploader } from "@coworker/apprestructured/src/uploader/useImageUploader";
import ViewerTopFragment, {
  ViewerTopFragmentCommonProps,
} from "./ViewerTop/viewer-top-fragment/ViewerTopFragment";
import {
  getTaskCommentsImagesQueryKey,
  getTaskCommentsQueryKey,
  invalidateTaskQueries,
} from "@coworker/apprestructured/src/task-management/services/queryKeyHelper";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import { useQueryClient } from "@tanstack/react-query";
import { useTaskFiles } from "@coworker/apprestructured/src/task-management/services/useTaskFiles";
import { FileItem } from "@coworker/apprestructured/src/task-management/components/Files/Files";
import { downloadFileFromUrl } from "@coworker/apprestructured/src/shared/helpers/downloadFileFromUrl";
import { formatFileSize } from "@coworker/apprestructured/src/shared/helpers/formatFileSize";
import {
  filesToNewFiles,
  NewFile,
} from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import { useTaskSchedule } from "../../services/useTaskSchedule";
import SomethingWentWrongPrompt from "@coworker/apprestructured/src/task-management/components/SomethingWentWrongPrompt/SomethingWentWrongPrompt";
import { TaskViewerMenuSheet } from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/TaskViewerMenuSheet/TaskViewerMenuSheet";
import { TaskViewerSheetHeader } from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/TaskViewerSheetHeader/TaskViewerSheetHeader";
import { TaskViewerSheetFooter } from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/TaskViewerSheetFooter/TaskViewerSheetFooter";
import { TaskViewerSheetContent } from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/TaskViewerSheetContent/TaskViewerSheetContent";
import { useUpdatePendingTasksInSchedule } from "@coworker/apprestructured/src/task-management/services/useUpdatePendingTasksInSchedule";
import { useAfterComplete } from "@coworker/apprestructured/src/task-management/services/useAfterComplete";

const NO_CLOSE_REASON_OPTIONS: CloseReasonOption[] = [];
const NO_FILE_ITEMS: FileItem[] = [];

/**
 * The ViewTaskSheet is open for the task with id 'taskViewer' taken from the url
 * search params. Open/close the sheet by adding/removing a 'taskViewer'
 * search param, or use the 'useSearchParamState' hook to reduce some boilerplate.
 */
function TaskViewerSheet() {
  const { t } = useTranslation();
  const [{ taskViewer: taskId }, setSearchParams] = useSearchParamState();
  const [closeReasonId, setCloseReasonId] = useState<string | null>(null);
  const [closeDescription, setCloseDescription] = useState("");
  const [patchTask, setPatchTask] = useState<PatchTask>({});
  const [showValidation, setShowValidation] = useState(false);

  const viewerFragmentRefs = useRef<Map<string, ViewerFragmentHandle>>(
    new Map()
  );

  const setViewerFragmentRef = useCallback(
    (key: string) => (element: ViewerFragmentHandle | null) => {
      if (element) {
        viewerFragmentRefs.current.set(key, element);
      } else {
        viewerFragmentRefs.current.delete(key);
      }
    },
    []
  );
  const [showCloseSheet, setShowCloseSheet] = useState(false);
  const [showMenuSheet, setShowMenuSheet] = useState(false);
  const [addCommentText, setAddCommentText] = useState("");
  const [addCommentImages, setAddCommentImages] = useState<File[]>([]);
  const [showCloseAllSheet, setShowCloseAllSheet] = useState(false);
  const myUserId = useUID();
  const myFullName = useUser(myUserId).data?.fullName;

  const saveGuardId = useId();

  const { enableSaveGuard, disableSaveGuard } = useSaveGuard(saveGuardId);

  const { data: allCloseReasons } = useAllCloseReasons();
  const {
    mutateAsync: updateTask,
    isPending: isSaving,
    error: updateTaskError,
  } = useUpdateTask();

  const {
    mutateAsync: updatePendingTasksInSchedule,
    isPending: isUpdatingPendingTasksInSchedule,
    error: updatePendingTasksInScheduleError,
  } = useUpdatePendingTasksInSchedule();

  const { data: allTaskTypes, isLoading: isLoadingTaskTypes } =
    useAllTaskTypes();
  const { data: originalTask, isFetching: isFetchingOriginalTask } =
    useTask(taskId);
  const { data: images } = useTaskImages(taskId, originalTask?.image_ids);

  const { data: fileUploads } = useTaskFiles(taskId, originalTask?.file_ids);
  const { data: schedule } = useTaskSchedule(taskId);

  const isTaskScheduled = !!originalTask?.schedule_id && !!schedule;

  const executeAfterComplete = useAfterComplete(originalTask);

  const addCommentImagesForImageUploader = useMemo<NewFile[]>(() => {
    return filesToNewFiles(addCommentImages);
  }, [addCommentImages]);

  const {
    ready: addCommentImageUploaderIsReady,
    createUploads: addCommentCreateUploads,
    upload: uploadAddCommentImages,
    error: commentImageUploaderError,
  } = useImageUploader(addCommentImagesForImageUploader);

  const {
    mutateAsync: createComment,
    isPending: isCreatingComment,
    error: createCommentError,
  } = useCreateComment();

  // Called similarly to the effect above, but before the task has finished being fetched.
  // This prevents showing stale changes applied through the patch task, when
  // fetching a task that is already in the cache.
  const handleTaskIdHasChanged = useEffectEvent(() => {
    setShowCloseSheet(false);
    setShowCloseAllSheet(false);
    setShowMenuSheet(false);
    setPatchTask({});
    setShowValidation(false);
  });

  useEffect(() => {
    handleTaskIdHasChanged();
  }, [taskId]);

  const taskType = useMemo(() => {
    if (!allTaskTypes || !originalTask) {
      return undefined;
    }
    return allTaskTypes.find(({ id }) => id === originalTask.task_type_id);
  }, [originalTask, allTaskTypes]);

  const closeReasonOptions = useMemo(() => {
    if (!allCloseReasons || !taskType) {
      return NO_CLOSE_REASON_OPTIONS;
    } else {
      return allCloseReasons
        .filter(({ id }) => taskType.close_reason_ids.includes(id))
        .map(({ id, label, requires_description: requiresDescription }) => {
          return {
            value: id,
            label: t(label),
            requiresDescription,
          };
        });
    }
  }, [allCloseReasons, t, taskType]);

  const handleCloseClick = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete("taskViewer");
    });
  }, [setSearchParams]);

  const hasUnsavedChanges = Object.keys(patchTask).length > 0;

  const queryClient = useQueryClient();

  const handleSaveAllTasksInSchedule = useCallback(
    (patchScheduleTask: PatchScheduleTask) => {
      async function fn() {
        if (originalTask) {
          disableSaveGuard();
          await updatePendingTasksInSchedule({
            scheduleId: originalTask.schedule_id!,
            patchScheduleTask: patchScheduleTask,
          });
          invalidateTaskQueries(queryClient);
          setPatchTask({});
        }
      }

      fn();
    },
    [disableSaveGuard, queryClient, updatePendingTasksInSchedule, originalTask]
  );

  const handleSave = useCallback(
    (patchTask: PatchTask) => {
      async function fn() {
        if (taskId) {
          disableSaveGuard();
          // The backend replaces `details` entirely rather than deep merging,
          // so we always send the full details object merged with any pending
          // changes. This also covers pick-up, where details must be present
          // even if the user hasn't changed anything.
          const effectivePatchTask = originalTask?.details
            ? {
                ...patchTask,
                details: {
                  ...(originalTask.details as Record<string, unknown>),
                  ...(patchTask.details ?? {}),
                },
              }
            : patchTask;
          await updateTask({
            id: taskId,
            patchTask: effectivePatchTask,
          });
          invalidateTaskQueries(queryClient);
          setPatchTask({});

          if (patchTask.state === "completed") {
            await executeAfterComplete();
          }
        }
      }

      fn();
    },
    [
      disableSaveGuard,
      queryClient,
      taskId,
      updateTask,
      originalTask,
      executeAfterComplete,
    ]
  );

  const handleSaveClick = useCallback(() => {
    handleSave(patchTask);
  }, [handleSave, patchTask]);

  const handleCompleteClick = useCallback(() => {
    const someFragmentsAreInvalid = Array.from(
      viewerFragmentRefs.current.values()
    ).some((handle) => !handle.valid);
    if (someFragmentsAreInvalid) {
      setShowValidation(true);
    } else {
      handleSave({ ...patchTask, state: "completed" });
    }
  }, [handleSave, patchTask]);

  const handlePickUpClick = useCallback(() => {
    handleSave({ ...patchTask, assigned_user_id: myUserId });
  }, [handleSave, myUserId, patchTask]);

  const handleMenuCloseButtonClick = useCallback(() => {
    setShowMenuSheet(false);
  }, []);

  const handleTopMenuButtonClick = useCallback(() => {
    setShowMenuSheet(true);
  }, []);

  const {
    saveGuardedFunction: handleCloseSheetClose,
    enableSaveGuard: enableCloseSheetCloseSaveGuard,
    disableSaveGuard: disableCloseSheetCloseSaveGuard,
  } = useSaveGuardedFunction(
    useCallback(() => {
      setShowCloseSheet(false);
      setShowCloseAllSheet(false);
    }, [])
  );

  const { saveGuardedFunction: handleCloseTaskMenuSheetClick } =
    useSaveGuardedFunction(
      useCallback(() => {
        setCloseReasonId(null);
        setCloseDescription("");
        setShowMenuSheet(false);
        setShowCloseSheet(true);
        setShowCloseAllSheet(false);
        setPatchTask({});
      }, []),
      saveGuardId
    );

  // Only create this handler when task is scheduled
  const handleCloseAllUnfinishedTasksMenuSheetClickCallback =
    useCallback(() => {
      if (isTaskScheduled) {
        setCloseReasonId(null);
        setCloseDescription("");
        setShowMenuSheet(false);
        setShowCloseSheet(false);
        setShowCloseAllSheet(true);
        setPatchTask({});
      }
    }, [isTaskScheduled]);

  const { saveGuardedFunction: handleCloseAllUnfinishedTasksMenuSheetClick } =
    useSaveGuardedFunction(
      handleCloseAllUnfinishedTasksMenuSheetClickCallback,
      saveGuardId
    );

  const { saveGuardedFunction: handleReopenClosedTaskMenuSheetClick } =
    useSaveGuardedFunction(
      useCallback(() => {
        if (originalTask) {
          setShowMenuSheet(false);
          handleSave({
            ...originalTask,
            state: "pending",
            close_reason_id: null,
            close_description: "",
          });
        }
      }, [handleSave, originalTask]),
      saveGuardId
    );

  const { saveGuardedFunction: handleReopenCompletedTaskMenuSheetClick } =
    useSaveGuardedFunction(
      useCallback(() => {
        if (originalTask) {
          setShowMenuSheet(false);
          handleSave({
            ...originalTask,
            state: "pending",
          });
        }
      }, [handleSave, originalTask]),
      saveGuardId
    );

  const { saveGuardedFunction: handleEditTaskMenuSheetClick } =
    useSaveGuardedFunction(
      useCallback(() => {
        if (originalTask) {
          setShowMenuSheet(false);
          setSearchParams((prev) => {
            prev.delete("taskViewer");
            prev.set("taskEditor", String(originalTask.id));
          });
        }
      }, [originalTask, setSearchParams]),
      saveGuardId
    );

  const { saveGuardedFunction: handleDropTaskMenuSheetClick } =
    useSaveGuardedFunction(
      useCallback(() => {
        if (originalTask) {
          setShowMenuSheet(false);
          handleSave({
            ...originalTask,
            assigned_user_id: null,
          });
        }
      }, [handleSave, originalTask]),
      saveGuardId
    );

  const handleCloseSheetCloseTask = useCallback(() => {
    if (originalTask) {
      setShowCloseSheet(false);
      setCloseReasonId(null);
      setCloseDescription("");
      disableCloseSheetCloseSaveGuard();
      handleSave({
        ...originalTask,
        state: "closed",
        close_reason_id: closeReasonId,
        close_description: closeDescription,
      });
    }
  }, [
    closeDescription,
    closeReasonId,
    disableCloseSheetCloseSaveGuard,
    handleSave,
    originalTask,
  ]);

  const handleCloseAllTasksInSchedule = useCallback(() => {
    // Only execute if task is actually scheduled
    if (isTaskScheduled && originalTask && schedule) {
      setShowCloseAllSheet(false);
      setCloseReasonId(null);
      setCloseDescription("");
      disableCloseSheetCloseSaveGuard();
      handleSaveAllTasksInSchedule({
        state: "closed",
        close_reason_id: closeReasonId,
        close_description: closeDescription,
      });
    }
  }, [
    isTaskScheduled,
    handleSaveAllTasksInSchedule,
    closeDescription,
    closeReasonId,
    disableCloseSheetCloseSaveGuard,
    schedule,
    originalTask,
  ]);

  const blockInput =
    isSaving ||
    isFetchingOriginalTask ||
    !taskId ||
    isUpdatingPendingTasksInSchedule;

  const menuSheet = () => {
    if (!originalTask) return null;
    return (
      <TaskViewerMenuSheet
        showMenuSheet={showMenuSheet}
        taskState={originalTask.state}
        isTaskScheduled={isTaskScheduled}
        assignedUserId={originalTask.assigned_user_id ?? ""}
        isDisabled={blockInput}
        closeReasonOptions={closeReasonOptions}
        onMenuCloseButtonClick={handleMenuCloseButtonClick}
        onEditTaskMenuSheetClick={handleEditTaskMenuSheetClick}
        onDropTaskMenuSheetClick={handleDropTaskMenuSheetClick}
        onCloseTaskMenuSheetClick={handleCloseTaskMenuSheetClick}
        onReopenClosedTaskMenuSheetClick={handleReopenClosedTaskMenuSheetClick}
        onReopenCompletedTaskMenuSheetClick={
          handleReopenCompletedTaskMenuSheetClick
        }
        onCloseAllUnfinishedTasksMenuSheetClick={
          handleCloseAllUnfinishedTasksMenuSheetClick
        }
      />
    );
  };

  const handleCloseReasonIdChange = useCallback(
    (newCloseReasonId: string | null) => {
      setCloseReasonId(newCloseReasonId);
      enableCloseSheetCloseSaveGuard();
    },
    [enableCloseSheetCloseSaveGuard]
  );

  const handleCloseDescriptionChange = useCallback(
    (newCloseDescription: string) => {
      setCloseDescription(newCloseDescription);
      enableCloseSheetCloseSaveGuard();
    },
    [enableCloseSheetCloseSaveGuard]
  );
  const closeSheet = useMemo(() => {
    return (
      <CloseSheet
        title={t("closeString")}
        subtitle={t("selectCloseReasonString")}
        buttonText={t("closeString")}
        options={closeReasonOptions}
        closeReasonId={closeReasonId}
        onCloseReasonIdChange={handleCloseReasonIdChange}
        description={closeDescription}
        onDescriptionChange={handleCloseDescriptionChange}
        visible={showCloseSheet}
        onClose={handleCloseSheetClose}
        onCloseTask={handleCloseSheetCloseTask}
      />
    );
  }, [
    t,
    closeDescription,
    closeReasonId,
    closeReasonOptions,
    handleCloseDescriptionChange,
    handleCloseReasonIdChange,
    handleCloseSheetClose,
    handleCloseSheetCloseTask,
    showCloseSheet,
  ]);

  const closeAllSheet = useMemo(() => {
    // Only create the component if task is scheduled
    if (!isTaskScheduled) return null;

    return (
      <CloseSheet
        options={closeReasonOptions}
        closeReasonId={closeReasonId}
        onCloseReasonIdChange={handleCloseReasonIdChange}
        description={closeDescription}
        onDescriptionChange={handleCloseDescriptionChange}
        onCloseTask={handleCloseAllTasksInSchedule}
        onClose={handleCloseSheetClose}
        visible={showCloseAllSheet}
        title={t("closeAllUnfinishedTasksSeriesString")}
        subtitle={t("selectReasonClosingString")}
        buttonText={t("closeAllUnfinishedTasksString")}
      />
    );
  }, [
    isTaskScheduled,
    closeReasonOptions,
    closeReasonId,
    handleCloseReasonIdChange,
    closeDescription,
    handleCloseDescriptionChange,
    handleCloseAllTasksInSchedule,
    handleCloseSheetClose,
    showCloseAllSheet,
    t,
  ]);

  const loadingFailed =
    !isFetchingOriginalTask &&
    !isLoadingTaskTypes &&
    (!originalTask || !taskType);

  const taskForFragments = useMemo(() => {
    return !originalTask ? LOADING : merge(originalTask, patchTask);
  }, [originalTask, patchTask]);

  const handleTaskChange = useCallback(
    (part: Record<string, unknown>) => {
      enableSaveGuard();
      setPatchTask((prev) => merge(prev, part));
    },
    [enableSaveGuard]
  );

  const handleMutationResults = useCallback(
    (
      createdCommentMutationResult: PromiseSettledResult<Comment>,
      uploadCommentImagesResult: PromiseSettledResult<void>
    ) => {
      if (createdCommentMutationResult.status === "fulfilled") {
        queryClient.setQueryData<Comment[]>(
          getTaskCommentsQueryKey(taskId!),
          (previousComments = []) => {
            return [...previousComments, createdCommentMutationResult.value];
          }
        );

        if (createdCommentMutationResult.value.image_ids.length > 0) {
          queryClient.invalidateQueries({
            queryKey: getTaskCommentsImagesQueryKey(taskId!),
          });
        }

        setAddCommentText("");
        setAddCommentImages([]);
      }
      if (uploadCommentImagesResult.status === "rejected") {
        const imageUploadIds =
          createdCommentMutationResult.status === "fulfilled"
            ? createdCommentMutationResult.value?.image_ids
            : "unknown";
        reportMessageToSentry(
          "Image upload failed",
          { uploadIds: imageUploadIds },
          "error"
        );
      }
    },
    [taskId, queryClient]
  );

  const handleAddCommentClick = useCallback(() => {
    async function fn() {
      const [createCommentMutationResult, uploadCommentImagesResult] =
        await Promise.allSettled([
          createComment({
            task_id: taskId!,
            text: addCommentText,
            images: addCommentCreateUploads,
            title: myFullName!,
            creator_user_id: myUserId,
          }),
          uploadAddCommentImages(),
        ]);
      handleMutationResults(
        createCommentMutationResult,
        uploadCommentImagesResult
      );
    }

    fn();
  }, [
    createComment,
    taskId,
    addCommentText,
    addCommentCreateUploads,
    myFullName,
    myUserId,
    uploadAddCommentImages,
    handleMutationResults,
  ]);

  const handleAddCommentTextChange = useCallback(
    (text: string) => {
      if (addCommentText.length === 0 && text.startsWith(" ")) {
        return;
      }
      setAddCommentText(text);
    },
    [addCommentText]
  );

  const viewerFragmentCommonProps: ViewerFragmentCommonProps = useMemo(() => {
    return {
      blockInput,
      task: taskForFragments,
      onTaskChange: handleTaskChange,
      showValidation,
    };
  }, [blockInput, handleTaskChange, taskForFragments, showValidation]);

  const viewerTopFragmentCommonProps =
    useMemo<ViewerTopFragmentCommonProps>(() => {
      return { task: taskForFragments };
    }, [taskForFragments]);

  const viewerTopFragment = useMemo(() => {
    if (!taskType) {
      return null;
    }
    return (
      <ViewerTopFragment
        config={taskType.viewer_top_fragment}
        commonProps={viewerTopFragmentCommonProps}
      />
    );
  }, [taskType, viewerTopFragmentCommonProps]);

  const recurrenceText = useMemo(() => {
    if (!schedule) {
      return null;
    }

    if (schedule.recurrence.period === "daily") {
      return t("repeatsDailyString");
    } else if (schedule.recurrence.period === "weekly") {
      return t("repeatsWeeklyString");
    } else if (schedule.recurrence.period === "monthly") {
      return t("repeatsMonthlyString");
    } else {
      return null;
    }
  }, [schedule, t]);

  const startElement = useMemo(() => {
    if (originalTask?.start) {
      return (
        <TitledText title={t("scheduledForString")} text={originalTask.start} />
      );
    } else {
      return null;
    }
  }, [originalTask, t]);

  const imagesElement = useMemo(() => {
    if (images !== undefined && images.length > 0) {
      return <Images urls={images.map(({ file }) => file.url)} />;
    } else if (
      originalTask !== undefined &&
      originalTask.image_ids.length > 0
    ) {
      return <Images urls={LOADING} />;
    } else {
      return null;
    }
  }, [images, originalTask]);

  const fileItems = useMemo<FileItem[]>(() => {
    if (fileUploads === undefined) {
      return NO_FILE_ITEMS;
    } else {
      return fileUploads.map(({ file }) => {
        return {
          name: file.name,
          size: formatFileSize(file.size),
          onDownload: async () => {
            try {
              // Should probably show some prompt here as well
              downloadFileFromUrl(file.url, file.name);
            } catch (e) {
              reportMessageToSentry(
                `Failed to download task file from upload '${file.id}'`,
                { error: e },
                "error"
              );
            }
          },
        };
      });
    }
  }, [fileUploads]);

  const handleRefreshClick = useCallback(() => {
    disableSaveGuard();
    setTimeout(() => {
      window.location.reload();
    }, 0);
  }, [disableSaveGuard]);

  const showSomethingWentWrongPrompt: boolean =
    !!updateTaskError ||
    !!createCommentError ||
    !!commentImageUploaderError ||
    !!updatePendingTasksInScheduleError;

  const content = () => {
    if (!taskId) return null;

    if (loadingFailed) {
      return (
        <NotFound
          title={t("sorryTaskCouldNotBeFoundString")}
          message={t("thisTaskDoesNotExistString")}
        />
      );
    } else if (!taskType) {
      return (
        <TaskViewerSheetHeader
          isLoading={!taskType}
          isNotFound={loadingFailed}
          task={taskForFragments}
          viewerTopFragment={viewerTopFragment}
          recurrenceText={recurrenceText}
          onCloseClick={handleCloseClick}
          onTopMenuButtonClick={handleTopMenuButtonClick}
        />
      );
    } else {
      return (
        <TaskViewerSheetContent
          headerElement={
            <TaskViewerSheetHeader
              isLoading={!taskType}
              isNotFound={loadingFailed}
              task={taskForFragments}
              viewerTopFragment={viewerTopFragment}
              recurrenceText={recurrenceText}
              onCloseClick={handleCloseClick}
              onTopMenuButtonClick={handleTopMenuButtonClick}
            />
          }
          taskId={taskId}
          imagesElement={imagesElement}
          viewerFragments={taskType.viewer_fragments}
          viewerFragmentCommonProps={viewerFragmentCommonProps}
          setViewerFragmentRef={setViewerFragmentRef}
          startElement={startElement}
          fileItems={fileItems}
          addCommentText={addCommentText}
          addCommentImages={addCommentImages}
          isCreatingComment={isCreatingComment}
          addCommentImageUploaderIsReady={addCommentImageUploaderIsReady}
          onAddCommentTextChange={handleAddCommentTextChange}
          setAddCommentImages={setAddCommentImages}
          onAddCommentClick={handleAddCommentClick}
        />
      );
    }
  };

  return (
    <>
      <FixaModal
        data-testid={"task-viewer-sheet"}
        visible={!!taskId}
        handleCloseBtn={handleCloseClick}
      >
        <FixaSheets
          ariaLabel={t("taskViewString")}
          className={`${styles["taskViewerSheet"]} ${loadingFailed ? "" : styles["greyBackground"]}`}
          header={null}
          footer={
            <TaskViewerSheetFooter
              showNoFooter={loadingFailed}
              originalTask={originalTask}
              isSaving={isSaving}
              isFetchingOriginalTask={isFetchingOriginalTask}
              hasUnsavedChanges={hasUnsavedChanges}
              isDisabled={blockInput}
              onSaveClick={handleSaveClick}
              onCompleteClick={handleCompleteClick}
              onPickUpClick={handlePickUpClick}
            />
          }
          fullSize
        >
          {content()}
        </FixaSheets>
      </FixaModal>
      {menuSheet()}
      {closeSheet}
      {showSomethingWentWrongPrompt && (
        <SomethingWentWrongPrompt onRefreshClick={handleRefreshClick} />
      )}
      {isTaskScheduled && closeAllSheet}
    </>
  );
}

export default TaskViewerSheet;
